import React, { useState } from 'react';
import Moment from 'moment';
import { Order, StatusResponse } from '../../models/APIModels';

interface StepDef {
  statusId: number;
  label: string;
  subLabel: string;
}

const STORAGE_STEPS: StepDef[] = [
  { statusId: 1, label: 'Order Placed', subLabel: 'Your order has been confirmed' },
  { statusId: 2, label: 'Paid', subLabel: 'Payment received' },
  { statusId: 3, label: 'Collected', subLabel: 'Your boxes have been collected' },
  { statusId: 4, label: 'Stored', subLabel: 'Your boxes are safely stored' },
  { statusId: 5, label: 'Return Scheduled', subLabel: 'Your boxes are being prepared for delivery' },
  { statusId: 8, label: 'Final Payment', subLabel: 'Final payment processed' },
  { statusId: 6, label: 'Delivered', subLabel: 'Your boxes have been delivered' },
];

const SHIPPING_STEPS: StepDef[] = [
  { statusId: 1, label: 'Order Placed', subLabel: 'Your order has been confirmed' },
  { statusId: 2, label: 'Paid', subLabel: 'Payment received' },
  { statusId: 6, label: 'Delivered', subLabel: 'Your order has been delivered' },
];

interface OrderProgressTrackerProps {
  order: Order;
  compact?: boolean;
}

export const OrderProgressTracker: React.FC<OrderProgressTrackerProps> = ({
  order,
  compact = false,
}) => {
  const [expandedStepIds, setExpandedStepIds] = useState<Set<number>>(new Set());

  if (order.orderType === 'removal') return null;

  const steps = order.storageRequired ? STORAGE_STEPS : SHIPPING_STEPS;
  const statuses: StatusResponse[] = order.statuses || [];

  // Build map: statusId → most recent StatusResponse
  const statusMap = new Map<number, StatusResponse>();
  statuses.forEach(s => {
    const existing = statusMap.get(s.statusId);
    if (!existing || s.updatedAt > existing.updatedAt) {
      statusMap.set(s.statusId, s);
    }
  });

  const hasStatus7 = statusMap.has(7);

  // Find highest index in steps array whose statusId is present
  let lastCompletedIndex = -1;
  steps.forEach((step, index) => {
    if (statusMap.has(step.statusId)) {
      lastCompletedIndex = index;
    }
  });

  const toggleStep = (statusId: number) => {
    setExpandedStepIds(prev => {
      const next = new Set(prev);
      if (next.has(statusId)) {
        next.delete(statusId);
      } else {
        next.add(statusId);
      }
      return next;
    });
  };

  const formatDate = (updatedAt: number): string => {
    if (!updatedAt) return '';
    return Moment(updatedAt).format('D MMM YYYY');
  };

  return (
    <div className={`order-progress-tracker${compact ? ' order-progress-tracker--compact' : ''}`}>
      {!compact && <p className="order-progress-tracker__heading">ORDER PROGRESS</p>}
      <div className="order-progress-tracker__steps">
        {steps.map((step, index) => {
          const isCompleted = statusMap.has(step.statusId);
          const isCurrent = !isCompleted && index === lastCompletedIndex + 1;
          const statusEntry = statusMap.get(step.statusId);
          const isExpanded = expandedStepIds.has(step.statusId);
          const connectorFilled = isCompleted;

          let subLabel = step.subLabel;
          if (step.statusId === 3 && !isCompleted && hasStatus7) {
            subLabel = 'Awaiting Collection';
          }

          let stepClass: string;
          if (isCompleted) {
            stepClass = 'order-progress-step--completed';
          } else if (isCurrent) {
            stepClass = 'order-progress-step--current';
          } else {
            stepClass = 'order-progress-step--future';
          }

          return (
            <React.Fragment key={step.statusId}>
              <div
                className={`order-progress-step ${stepClass}`}
                onClick={() => {
                  if (isCompleted && !compact) {
                    toggleStep(step.statusId);
                  }
                }}
                role={isCompleted && !compact ? 'button' : undefined}
                aria-expanded={isCompleted && !compact ? isExpanded : undefined}
              >
                <div className="order-progress-step__indicator">
                  {isCompleted ? (
                    <span className="order-progress-step__check">✓</span>
                  ) : isCurrent ? (
                    <span className="order-progress-step__pulse" />
                  ) : (
                    <span className="order-progress-step__circle" />
                  )}
                </div>
                <div className="order-progress-step__content">
                  <span className="order-progress-step__label">{step.label}</span>
                  {!compact && isCurrent && subLabel && (
                    <span className="order-progress-step__sublabel">{subLabel}</span>
                  )}
                  {!compact && isCompleted && isExpanded && statusEntry && (
                    <span className="order-progress-step__date">
                      {formatDate(statusEntry.updatedAt)}
                    </span>
                  )}
                  {!compact && isCompleted && (
                    <span className="order-progress-step__toggle">
                      {isExpanded ? '▲' : '▼'}
                    </span>
                  )}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`order-progress-connector${connectorFilled ? ' order-progress-connector--filled' : ''}`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
